import { useCallback, type ChangeEvent } from 'react';
import type { DiscordEmbedField, EmbedFormState } from './types';

interface Props {
  value: EmbedFormState;
  onChange: (next: EmbedFormState) => void;
  className?: string;
}

export function EmbedEditor({ value, onChange, className }: Props) {
  const update = useCallback(
    <K extends keyof EmbedFormState>(key: K, v: EmbedFormState[K]) => {
      onChange({ ...value, [key]: v });
    },
    [value, onChange],
  );

  const updateField = useCallback(
    (index: number, patch: Partial<DiscordEmbedField>) => {
      const next = value.fields.slice();
      const current = next[index];
      if (!current) return;
      next[index] = { ...current, ...patch };
      onChange({ ...value, fields: next });
    },
    [value, onChange],
  );

  const addField = useCallback(() => {
    if (value.fields.length >= 25) return;
    onChange({
      ...value,
      fields: [...value.fields, { name: '', value: '', inline: false }],
    });
  }, [value, onChange]);

  const removeField = useCallback(
    (index: number) => {
      onChange({ ...value, fields: value.fields.filter((_, i) => i !== index) });
    },
    [value, onChange],
  );

  return (
    <div className={'space-y-4 ' + (className ?? '')}>
      <Section title="Title and body">
        <Field label="Title">
          <TextInput
            value={value.title}
            onChange={(e) => update('title', e.target.value)}
            maxLength={256}
            placeholder="Title (up to 256 chars)"
          />
        </Field>
        <Field label="Title URL">
          <TextInput
            value={value.url}
            onChange={(e) => update('url', e.target.value)}
            placeholder="https://…"
          />
        </Field>
        <Field label="Description">
          <textarea
            value={value.description}
            onChange={(e) => update('description', e.target.value)}
            rows={5}
            maxLength={4096}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder="Body text (up to 4096, Markdown supported)"
          />
        </Field>
        <div className="flex flex-wrap items-center gap-4">
          <Field label="Color" inline>
            <input
              type="color"
              value={value.colorHex || '#5865F2'}
              onChange={(e) => update('colorHex', e.target.value)}
              className="h-9 w-12 cursor-pointer rounded-md border border-input bg-background"
            />
            <TextInput
              value={value.colorHex}
              onChange={(e) => update('colorHex', e.target.value)}
              className="w-28"
              placeholder="#5865F2"
            />
          </Field>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={value.withTimestamp}
              onChange={(e) => update('withTimestamp', e.target.checked)}
            />
            Timestamp (now)
          </label>
        </div>
      </Section>

      <Section title="Author">
        <Field label="Name">
          <TextInput
            value={value.authorName}
            onChange={(e) => update('authorName', e.target.value)}
            maxLength={256}
            placeholder="Author name"
          />
        </Field>
        <Field label="URL">
          <TextInput
            value={value.authorUrl}
            onChange={(e) => update('authorUrl', e.target.value)}
            placeholder="https://…"
          />
        </Field>
        <Field label="Icon URL">
          <TextInput
            value={value.authorIcon}
            onChange={(e) => update('authorIcon', e.target.value)}
            placeholder="https://…/icon.png"
          />
        </Field>
      </Section>

      <Section title="Footer">
        <Field label="Text">
          <TextInput
            value={value.footerText}
            onChange={(e) => update('footerText', e.target.value)}
            maxLength={2048}
            placeholder="Footer text"
          />
        </Field>
        <Field label="Icon URL">
          <TextInput
            value={value.footerIcon}
            onChange={(e) => update('footerIcon', e.target.value)}
            placeholder="https://…/icon.png"
          />
        </Field>
      </Section>

      <Section title="Images">
        <Field label="Image URL">
          <TextInput
            value={value.imageUrl}
            onChange={(e) => update('imageUrl', e.target.value)}
            placeholder="https://…/image.png"
          />
        </Field>
        <Field label="Thumbnail URL">
          <TextInput
            value={value.thumbnailUrl}
            onChange={(e) => update('thumbnailUrl', e.target.value)}
            placeholder="https://…/thumb.png"
          />
        </Field>
      </Section>

      <Section title={`Fields (${value.fields.length}/25)`}>
        <div className="space-y-3">
          {value.fields.map((f, i) => (
            <div key={i} className="rounded-md border border-input p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-muted-foreground">Field #{i + 1}</span>
                <button
                  type="button"
                  onClick={() => removeField(i)}
                  className="text-xs text-destructive hover:underline"
                >
                  remove
                </button>
              </div>
              <TextInput
                value={f.name}
                onChange={(e) => updateField(i, { name: e.target.value })}
                maxLength={256}
                placeholder="Name"
              />
              <textarea
                value={f.value}
                onChange={(e) => updateField(i, { value: e.target.value })}
                rows={2}
                maxLength={1024}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Value"
              />
              <label className="flex cursor-pointer items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={Boolean(f.inline)}
                  onChange={(e) => updateField(i, { inline: e.target.checked })}
                />
                inline
              </label>
            </div>
          ))}
          {value.fields.length < 25 && (
            <button
              type="button"
              onClick={addField}
              className="rounded-md border border-dashed border-input px-3 py-2 text-sm text-muted-foreground hover:bg-accent"
            >
              + add field
            </button>
          )}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <div className="space-y-2 rounded-md border border-border bg-card p-3">{children}</div>
    </div>
  );
}

function Field({
  label,
  children,
  inline,
}: {
  label: string;
  children: React.ReactNode;
  inline?: boolean;
}) {
  return (
    <div className={inline ? 'flex items-center gap-2' : 'space-y-1'}>
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <div className={inline ? 'flex items-center gap-2' : ''}>{children}</div>
    </div>
  );
}

interface TextInputProps {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  maxLength?: number;
}

function TextInput({ value, onChange, placeholder, className, maxLength }: TextInputProps) {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      maxLength={maxLength}
      placeholder={placeholder}
      className={
        'block h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
        (className ?? '')
      }
    />
  );
}
